import { getImageUrl } from '@/lib/image-utils';
import { RoomType, FurnitureStyle, roomTypeMapping, furnitureStyleMapping } from '../components/virtual-staging-widget';

// Interface for credit details
export interface CreditDetails {
  userId: string;
  designsLeft: number;
  totalDesigns: number;
  canDesign: boolean;
}

// Interface for uploaded asset
export interface Asset {
  id: string;
  asset_id: string;
}

// Interface for virtual staging response
export interface StagingResponse {
  success: boolean;
  result_image_url: string;
  message?: string;
}

// Interface for save design response
export interface SaveDesignResponse {
  success: boolean;
  saved_design_id: string;
  message?: string;
}

// API URL should be configurable based on environment
// Use the proxy for development to avoid CORS issues
const isDevelopment = (import.meta as any).env.DEV;
const API_BASE_URL = isDevelopment ? 'http://localhost:8080/ai-widget/dzinly' : 'https://api.tech.renovateai.app/ai-widget/dzinly';  // Production URL
// No separate API key is needed, we'll use the client key for all requests

// Default error handler
const handleApiError = (error: unknown, defaultMessage: string) => {
  console.error(defaultMessage, error);
  if (error instanceof Error) {
    throw new Error(`${defaultMessage}: ${error.message}`);
  }
  throw new Error(defaultMessage);
};

// Helper function to get the origin header for API requests
export function getOriginHeader(): string {
  try {
    // Get origin from URL parameter if available
    const urlParams = new URLSearchParams(window.location.search);
    const originParam = urlParams.get('origin');

    if (originParam) {
      return originParam;
    }
  } catch (error) {
    console.error('Error getting origin from URL parameter:', error);
  }

  // Fallback to current window origin
  return window.location.origin;
}

/**
 * Get user credit details
 */
export async function getUserCredits(
  clientKey: string,
  { userId }: { userId: string }
): Promise<CreditDetails> {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const response = await fetch(`${API_BASE_URL}/user/credits?user_id=${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${clientKey}`,
        'Content-Type': 'application/json',
        'X-API-KEY': clientKey,
        'X-CLIENT-KEY': clientKey,
        'X-ORIGIN': getOriginHeader()
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.credits?.result || data;
  } catch (error) {
    console.error('Error fetching user credits:', error);
    // Return fallback data
    return {
      userId: userId || '',
      designsLeft: 3,
      totalDesigns: 3,
      canDesign: true
    };
  }
}

/**
 * Upload an image from URL
 */
export async function uploadAssetFromUrl(imageUrl: string, clientKey?: string): Promise<Asset> {
  const formData = new FormData();
  formData.append('image_url', imageUrl);

  try {
    const response = await fetch(`${API_BASE_URL}/assets/upload`, {
      method: 'POST',
      headers: {
        'X-API-KEY': clientKey || '',
        'X-CLIENT-KEY': clientKey || '',
        'X-ORIGIN': getOriginHeader()
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Asset upload failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.asset as Asset;
  } catch (error) {
    console.error('Error uploading asset from URL:', error);
    throw error;
  }
}

/**
 * Upload a file directly
 */
export async function uploadFile(file: File, clientKey?: string): Promise<Asset> {
  const formData = new FormData();
  formData.append('image', file);

  try {
    const response = await fetch(`${API_BASE_URL}/assets/upload`, {
      method: 'POST',
      headers: {
        'X-API-KEY': clientKey || '',
        'X-CLIENT-KEY': clientKey || '',
        'X-ORIGIN': getOriginHeader()
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Asset upload failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.asset || {
      id: data.asset_id || '',
      asset_id: data.asset_id || '',
      preview_url: data.preview_url || URL.createObjectURL(file)
    };
  } catch (error) {
    console.error('Error uploading asset:', error);
    throw error;
  }
}

/**
 * Process virtual staging
 */
export async function processVirtualStaging(
  assetId: string,
  originalImageUrl: string,
  roomType: RoomType,
  style: FurnitureStyle,
  autoDeclutter: boolean = true,
  userId?: string,
  extraParams?: Record<string, any>,
  clientKey?: string
): Promise<StagingResponse> {
  try {
    // Create default extra params
    const defaultExtra = {
      project_id: 0,
      tmp_project_id: 0,
      view_id: 0,
      project_view_id: 0
    };

    // Merge with provided extra params if any
    const mergedExtra = extraParams ? { ...defaultExtra, ...extraParams } : defaultExtra;

    const renovateDto = {
      asset_id: assetId,
      room_type: roomTypeMapping[roomType],
      style: furnitureStyleMapping[style],
      check_mls_compliant: autoDeclutter,
      user_id: userId || undefined,
      image_url: originalImageUrl,
      extra: mergedExtra
    };

    const response = await fetch(`${API_BASE_URL}/virtual-staging`, {
      method: 'POST',
      headers: {
        'X-API-KEY': clientKey || '',
        'X-CLIENT-KEY': clientKey || '',
        'Content-Type': 'application/json',
        'X-ORIGIN': getOriginHeader()
      },
      body: JSON.stringify(renovateDto),
    });

    if (!response.ok) {
      throw new Error(`Virtual staging failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    return {
      success: true,
      result_image_url: data.result_image_url || ''
    };
  } catch (error) {
    console.error('Error processing virtual staging:', error);
    throw error;
  }
}

/**
 * Save a design
 */
export async function saveDesign(
  name: string,
  originalImageUrl: string,
  resultImageUrl: string,
  roomType: RoomType,
  style: FurnitureStyle,
  userId?: string,
  clientKey?: string
): Promise<SaveDesignResponse> {
  try {
    const payload = {
      name,
      original_image_url: originalImageUrl,
      result_image_url: resultImageUrl,
      room_type: roomTypeMapping[roomType],
      style: furnitureStyleMapping[style],
      user_id: userId
    };

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-KEY': clientKey || '',
      'X-CLIENT-KEY': clientKey || '',
      'X-ORIGIN': getOriginHeader()
    };

    const response = await fetch(`${API_BASE_URL}/save-design`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`Failed to save design: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.message || 'Failed to save design: Unknown error');
    }

    return {
      success: true,
      saved_design_id: data.design_id || `local-${Date.now()}`,
      message: data.message || 'Design saved successfully'
    };
  } catch (error) {
    handleApiError(error, 'Failed to save design');
    throw error;
  }
}